import { BrandGroup, WorkspaceGroup } from '@models/types'

import { getMaximalUserPermissionLevel, userHasPermission } from './permission-utils'
import { authenticatedPaths } from './route-utils'
import { buildAuthenticatedRoute } from './url-utils'

export type NavCatalogGroup = 'publication' | 'pages' | 'newsletters' | 'settings' | 'workspace' | 'actions'

export type PublicationMenuItem = {
  name: string
  path: string
  iconName?: string
  children?: PublicationMenuItem[]
}

export type SettingsMenuItem = {
  name: string
  labelKey: string
  path: string
  iconName: string
  /**
   * Extra search terms for Cmd+K (features on the page that aren't the menu label).
   * Example: Domain also hosts email/sending-domain settings.
   */
  aliases?: string[]
}

/**
 * Settings subsections that aren't their own sidebar item, but users still search for them.
 * These resolve to the parent settings page (optionally with a hash once the page supports it).
 */
export type SettingsSubsectionEntry = {
  id: string
  labelKey: string
  path: string
  iconName: string
  aliases: string[]
  /** Only show when newsletter provider is CUSTOM (PubGen). */
  requireCustomNewsletter?: boolean
}

export type CommandPaletteContext = {
  workspace?: string
  brand?: string
  siteId?: string
  workspacePermissions?: WorkspaceGroup[]
  brandPermissions?: BrandGroup[]
  newsletterProvider?: string | null
}

export type CommandPaletteEntry = {
  id: string
  group: NavCatalogGroup
  /** Translation namespace for the visible label. */
  labelNamespace: 'PLMainMenu' | 'PLSettingsMenu' | 'PLCommandPalette'
  labelKey: string
  href: string
  iconName?: string
  /** Extra English search terms (path fragments, aliases). */
  keywords: string[]
}

export const PUB_MENU_ITEMS_BUILDER: PublicationMenuItem[] = [
  { name: 'writers', path: authenticatedPaths.siteWriters, iconName: 'draw' },
  { name: 'sections', path: authenticatedPaths.siteSections, iconName: 'label_important' },
  { name: 'tags', path: authenticatedPaths.siteTags, iconName: 'label' },
  { name: 'spotlights', path: authenticatedPaths.siteSpotlight, iconName: 'category' },
  {
    name: 'pages',
    iconName: 'storefront',
    path: authenticatedPaths.siteFrontPage,
    children: [
      { name: 'frontPage', path: authenticatedPaths.siteFrontPage },
      { name: 'account', path: authenticatedPaths.siteAccountPageEdit },
      { name: 'contactUs', path: authenticatedPaths.siteContactUs },
      { name: 'gift', path: authenticatedPaths.siteGift },
      { name: 'newsletterSignup', path: authenticatedPaths.siteNewsletters },
      { name: 'events', path: authenticatedPaths.siteEvents },
      { name: 'privacyPolicy', path: authenticatedPaths.sitePrivacyPolicy },
      { name: 'subscribe', path: authenticatedPaths.siteSubscribe },
      { name: 'search', path: authenticatedPaths.siteSearch },
      { name: 'termsAndConditions', path: authenticatedPaths.siteTermsAndConditions },
      { name: 'thankYou', path: authenticatedPaths.siteThankYou },
    ],
  },
  { name: 'templates', path: authenticatedPaths.siteTemplate, iconName: 'view_quilt' },
  { name: 'menus', path: authenticatedPaths.siteMenus, iconName: 'map' },
]

export const buildNewslettersMenuItem = (isCustomProvider: boolean): PublicationMenuItem => ({
  name: 'newsletters',
  iconName: 'mail',
  path: authenticatedPaths.siteNewsletterSystem,
  children: [
    { name: 'newsletters', path: authenticatedPaths.siteNewsletterSystem },
    ...(isCustomProvider
      ? [
          { name: 'sends', path: authenticatedPaths.siteNewsletterSystemSends },
          { name: 'contacts', path: authenticatedPaths.siteNewsletterSystemContacts },
          { name: 'segments', path: authenticatedPaths.siteNewsletterSystemSegments },
          { name: 'automations', path: authenticatedPaths.siteNewsletterSystemAutomations },
          { name: 'siteDisplay', path: authenticatedPaths.siteNewsletterSystemDisplay },
        ]
      : []),
    { name: 'settings', path: authenticatedPaths.siteNewsletterSystemSettings },
  ],
})

export const PUB_MENU_ITEMS_CREATOR_START: PublicationMenuItem[] = [
  { name: 'stories', path: authenticatedPaths.siteArticles, iconName: 'article' },
]

export const PUB_MENU_ITEMS_CREATOR_END: PublicationMenuItem[] = [
  { name: 'collections', path: authenticatedPaths.siteCollections, iconName: 'collections' },
  { name: 'assets', path: authenticatedPaths.siteAssets, iconName: 'image' },
  { name: 'analytics', path: authenticatedPaths.siteAnalytics, iconName: 'analytics' },
]

export const PUB_MENU_ITEMS_BUILDER_END: PublicationMenuItem[] = [
  { name: 'chats', path: authenticatedPaths.siteChats, iconName: 'chat' },
  { name: 'reports', path: authenticatedPaths.siteReports, iconName: 'summarize' },
  { name: 'university', path: authenticatedPaths.siteUniversity, iconName: 'school' },
  { name: 'support', path: authenticatedPaths.support, iconName: 'support_agent' },
]

export const PUB_SETTINGS_BUILDER_MENU_ITEMS: SettingsMenuItem[] = [
  {
    name: 'Associated Press',
    labelKey: 'associatedPress',
    path: authenticatedPaths.siteSettingsAP,
    iconName: 'newspaper',
  },
  {
    name: 'Reuters',
    labelKey: 'reuters',
    path: authenticatedPaths.siteSettingsReuters,
    iconName: 'settings_input_antenna',
  },
  {
    name: 'Publication Details',
    labelKey: 'publicationDetails',
    path: authenticatedPaths.siteSettingsGeneral,
    iconName: 'home',
  },
  { name: 'Feeds', labelKey: 'feeds', path: authenticatedPaths.siteSettingsFeeds, iconName: 'rss_feed' },
  {
    name: 'Third-Party Analytics',
    labelKey: 'thirdPartyAnalytics',
    path: authenticatedPaths.siteSettingsThirdPartyAnalytics,
    iconName: 'analytics',
  },
  {
    name: 'Advertisement',
    labelKey: 'advertisement',
    path: authenticatedPaths.siteSettingsAdvertisement,
    iconName: 'ads_click',
  },
  {
    name: 'Print on Demand',
    labelKey: 'printOnDemand',
    path: authenticatedPaths.siteSettingsGelato,
    iconName: 'local_shipping',
    aliases: ['gelato', 'print', 'pod'],
  },
  {
    name: 'Weather API',
    labelKey: 'weatherApi',
    path: authenticatedPaths.siteSettingsWeather,
    iconName: 'wb_sunny',
  },
  {
    name: 'Subscribers and Plans',
    labelKey: 'subscribersAndPlans',
    path: authenticatedPaths.siteSettingsPricing,
    iconName: 'people',
    aliases: ['stripe', 'pricing', 'subscriptions', 'gifts', 'promotions', 'paywall', 'plans'],
  },
  {
    name: 'Domain',
    labelKey: 'domain',
    path: authenticatedPaths.siteSettingsCustomDomain,
    iconName: 'dns',
    aliases: [
      'custom domain',
      'dns',
      'nameservers',
      'ssl',
      'email',
      'email domain',
      'sending domain',
      'newsletter domain',
      'transactional email',
      'email sending',
    ],
  },
  {
    name: 'Styles',
    labelKey: 'styles',
    path: authenticatedPaths.siteSettingsStyles,
    iconName: 'brush',
    aliases: ['fonts', 'colors', 'branding', 'theme', 'typography'],
  },
  {
    name: 'Chatbot CMS',
    labelKey: 'chatbotCms',
    path: authenticatedPaths.siteSettingsChatbotCMS,
    iconName: 'smart_toy',
    aliases: ['ai chatbot', 'cms chatbot'],
  },
  {
    name: 'Chatbot Publication',
    labelKey: 'chatbotPublication',
    path: authenticatedPaths.siteSettingsChatbotPublication,
    iconName: 'smart_toy',
    aliases: ['ai chatbot', 'public chatbot', 'site chatbot'],
  },
  { name: 'Redirects', labelKey: 'redirects', path: authenticatedPaths.siteSettingsRedirects, iconName: 'link' },
  {
    name: 'Story Generation',
    labelKey: 'storyGeneration',
    path: authenticatedPaths.siteSettingsArticleGeneration,
    iconName: 'article',
    aliases: ['ai', 'ai rules', 'context templates', 'article generation'],
  },
  {
    name: 'Users and Permissions',
    labelKey: 'usersAndPermissions',
    path: authenticatedPaths.siteSettingsUsers,
    iconName: 'groups',
    aliases: ['roles', 'permissions', 'invite', 'editors', 'writers'],
  },
  {
    name: 'Third Party Feeds',
    labelKey: 'thirdPartyFeeds',
    path: authenticatedPaths.siteSettingsExternalFeeds,
    iconName: 'rss_feed',
    aliases: ['external feeds', 'rss import'],
  },
]

/** Deep-linkable settings subsections users search for by feature name. */
export const SETTINGS_SUBSECTION_ENTRIES: SettingsSubsectionEntry[] = [
  {
    id: 'settings-subsection-email-sending',
    labelKey: 'emailSendingDomain',
    path: authenticatedPaths.siteSettingsCustomDomain,
    iconName: 'mail',
    aliases: [
      'email domain',
      'email settings',
      'sending domain',
      'newsletter sending domain',
      'transactional email',
      'transactional sending domain',
      'dns email',
      'ses',
    ],
  },
]

export const PUB_SETTINGS_ADMIN_MENU_ITEMS: SettingsMenuItem[] = [
  {
    name: 'Mobile App',
    labelKey: 'mobileApp',
    path: authenticatedPaths.siteSettingsMobileApp,
    iconName: 'phone_iphone',
  },
  {
    name: 'Data Imports',
    labelKey: 'dataImports',
    path: authenticatedPaths.siteSettingsDataImports,
    iconName: 'cloud_download',
  },
  {
    name: 'Data Export',
    labelKey: 'dataExport',
    path: authenticatedPaths.siteSettingsDataExports,
    iconName: 'cloud_upload',
  },
  {
    name: 'Setup Status',
    labelKey: 'setupStatus',
    path: authenticatedPaths.siteSettingsSetupStatus,
    iconName: 'checklist',
  },
]

export const PUB_SETTINGS_ALL_MENU_ITEMS = [...PUB_SETTINGS_ADMIN_MENU_ITEMS, ...PUB_SETTINGS_BUILDER_MENU_ITEMS]

export const getPublicationMenuItems = ({
  permissionLevel,
  newsletterProvider,
}: {
  permissionLevel: WorkspaceGroup | BrandGroup
  newsletterProvider?: string | null
}): PublicationMenuItem[] => {
  if (permissionLevel === 'Admin' || permissionLevel === 'Builder' || permissionLevel === 'Accounting') {
    return [
      ...PUB_MENU_ITEMS_CREATOR_START,
      ...PUB_MENU_ITEMS_BUILDER,
      buildNewslettersMenuItem(newsletterProvider === 'CUSTOM'),
      ...PUB_MENU_ITEMS_CREATOR_END,
      ...PUB_MENU_ITEMS_BUILDER_END,
    ]
  }
  if (permissionLevel === 'Creator' || permissionLevel === 'Guest') {
    return [...PUB_MENU_ITEMS_CREATOR_START, ...PUB_MENU_ITEMS_CREATOR_END]
  }
  return []
}

const flattenMenuItems = (
  items: PublicationMenuItem[],
  groupFor: (item: PublicationMenuItem, parent?: PublicationMenuItem) => NavCatalogGroup,
  parent?: PublicationMenuItem,
): Array<{ item: PublicationMenuItem; group: NavCatalogGroup; parent?: PublicationMenuItem }> => {
  const out: Array<{ item: PublicationMenuItem; group: NavCatalogGroup; parent?: PublicationMenuItem }> = []
  for (const item of items) {
    if (item.children?.length) {
      // Parent groups are navigable via their first child path; index children instead.
      for (const child of item.children) {
        out.push({ item: child, group: groupFor(child, item), parent: item })
      }
    } else {
      out.push({ item, group: groupFor(item, parent), parent })
    }
  }
  return out
}

const publicationGroupFor = (item: PublicationMenuItem, parent?: PublicationMenuItem): NavCatalogGroup => {
  if (parent?.name === 'pages' || item.name === 'pages') return 'pages'
  if (parent?.name === 'newsletters' || item.name === 'newsletters' || item.path.startsWith('/newsletters')) {
    return 'newsletters'
  }
  return 'publication'
}

const siteHref = (ctx: CommandPaletteContext, pageSlug: string): string | null => {
  if (!ctx.workspace || !ctx.brand || !ctx.siteId) return null
  return buildAuthenticatedRoute({
    workspace: ctx.workspace,
    brand: ctx.brand,
    siteId: ctx.siteId,
    pageSlug,
  })
}

const workspaceHref = (ctx: CommandPaletteContext, pageSlug?: string): string | null => {
  if (!ctx.workspace) return null
  return buildAuthenticatedRoute({ workspace: ctx.workspace, pageSlug })
}

/**
 * Flattened, permission-filtered destinations for the Cmd+K palette.
 * Labels are resolved by the UI via `labelNamespace` + `labelKey`.
 */
export const getCommandPaletteEntries = (ctx: CommandPaletteContext): CommandPaletteEntry[] => {
  const permissionLevel = getMaximalUserPermissionLevel({
    workspacePermissions: ctx.workspacePermissions,
    brandPermissions: ctx.brandPermissions,
  })
  const entries: CommandPaletteEntry[] = []
  const hasSite = !!(ctx.workspace && ctx.brand && ctx.siteId)
  const hasWorkspace = !!ctx.workspace

  if (hasSite) {
    const menuItems = getPublicationMenuItems({
      permissionLevel,
      newsletterProvider: ctx.newsletterProvider,
    })
    for (const { item, group } of flattenMenuItems(menuItems, publicationGroupFor)) {
      const href = siteHref(ctx, item.path)
      if (!href) continue
      entries.push({
        id: `pub-${item.path}`,
        group,
        labelNamespace: 'PLMainMenu',
        labelKey: item.name,
        href,
        iconName: item.iconName,
        keywords: [item.path.replace(/^\//, '').replace(/\//g, ' '), item.name],
      })
    }

    if (userHasPermission(permissionLevel, 'Builder')) {
      const settingsItems = permissionLevel === 'Admin' ? PUB_SETTINGS_ALL_MENU_ITEMS : PUB_SETTINGS_BUILDER_MENU_ITEMS
      for (const item of settingsItems) {
        const href = siteHref(ctx, item.path)
        if (!href) continue
        entries.push({
          id: `settings-${item.path}`,
          group: 'settings',
          labelNamespace: 'PLSettingsMenu',
          labelKey: item.labelKey,
          href,
          iconName: item.iconName,
          keywords: [
            item.name,
            item.path.replace(/^\//, '').replace(/\//g, ' '),
            'settings',
            ...(item.aliases || []),
          ],
        })
      }

      for (const subsection of SETTINGS_SUBSECTION_ENTRIES) {
        if (subsection.requireCustomNewsletter && ctx.newsletterProvider !== 'CUSTOM') continue
        const href = siteHref(ctx, subsection.path)
        if (!href) continue
        entries.push({
          id: subsection.id,
          group: 'settings',
          labelNamespace: 'PLCommandPalette',
          labelKey: subsection.labelKey,
          href,
          iconName: subsection.iconName,
          keywords: [...subsection.aliases, 'settings'],
        })
      }
    }

    // Create actions available in publication context.
    const newStoryHref = siteHref(ctx, `${authenticatedPaths.siteArticles}/new-story`)
    if (newStoryHref) {
      entries.push({
        id: 'action-new-story',
        group: 'actions',
        labelNamespace: 'PLCommandPalette',
        labelKey: 'newStory',
        href: newStoryHref,
        iconName: 'add',
        keywords: ['new story', 'create story', 'new article'],
      })
    }

    if (permissionLevel === 'Admin' || permissionLevel === 'Accounting') {
      const billingHref = siteHref(ctx, authenticatedPaths.billing)
      if (billingHref) {
        entries.push({
          id: 'pub-billing',
          group: 'workspace',
          labelNamespace: 'PLCommandPalette',
          labelKey: 'billing',
          href: billingHref,
          iconName: 'payment',
          keywords: ['billing', 'payment', 'invoice'],
        })
      }
    }
  }

  if (hasWorkspace) {
    const publicationsHref = workspaceHref(ctx)
    if (publicationsHref) {
      entries.push({
        id: 'workspace-publications',
        group: 'workspace',
        labelNamespace: 'PLCommandPalette',
        labelKey: 'publications',
        href: publicationsHref,
        iconName: 'fiber_manual_record',
        keywords: ['publications', 'workspace', 'current workspace'],
      })
    }

    const isWorkspaceAdmin =
      ctx.workspacePermissions?.includes('Admin') || ctx.workspacePermissions?.includes('Accounting')

    if (isWorkspaceAdmin) {
      const workspaceAdminDestinations: Array<{
        id: string
        labelKey: string
        pageSlug: string
        iconName: string
        keywords: string[]
      }> = [
        {
          id: 'workspace-settings',
          labelKey: 'workspaceSettings',
          pageSlug: authenticatedPaths.workspaceSettings,
          iconName: 'settings',
          keywords: ['workspace settings'],
        },
        {
          id: 'workspace-feeds',
          labelKey: 'workspaceFeeds',
          pageSlug: authenticatedPaths.workspaceFeeds,
          iconName: 'rss_feed',
          keywords: ['workspace feeds'],
        },
        {
          id: 'workspace-ai-rules',
          labelKey: 'workspaceAiRules',
          pageSlug: authenticatedPaths.workspaceAiRules,
          iconName: 'gavel',
          keywords: ['workspace ai rules', 'ai rules'],
        },
        {
          id: 'workspace-analytics',
          labelKey: 'workspaceAnalytics',
          pageSlug: authenticatedPaths.workspaceAnalytics,
          iconName: 'stacked_line_chart',
          keywords: ['workspace analytics'],
        },
        {
          id: 'workspace-support-plans',
          labelKey: 'supportPlans',
          pageSlug: authenticatedPaths.supportPlans,
          iconName: 'support_agent',
          keywords: ['support plans'],
        },
        {
          id: 'workspace-new-publication',
          labelKey: 'newPublication',
          pageSlug: authenticatedPaths.newPublication,
          iconName: 'add',
          keywords: ['new publication', 'create publication'],
        },
      ]

      for (const dest of workspaceAdminDestinations) {
        const href = workspaceHref(ctx, dest.pageSlug)
        if (!href) continue
        entries.push({
          id: dest.id,
          group: dest.id === 'workspace-new-publication' ? 'actions' : 'workspace',
          labelNamespace: 'PLCommandPalette',
          labelKey: dest.labelKey,
          href,
          iconName: dest.iconName,
          keywords: dest.keywords,
        })
      }
    }
  }

  entries.push({
    id: 'account',
    group: 'workspace',
    labelNamespace: 'PLCommandPalette',
    labelKey: 'myAccount',
    href: authenticatedPaths.account,
    iconName: 'account_circle',
    keywords: ['account', 'my account', 'profile'],
  })

  entries.push({
    id: 'all-workspaces',
    group: 'workspace',
    labelNamespace: 'PLCommandPalette',
    labelKey: 'allWorkspaces',
    href: authenticatedPaths.workspaceHome,
    iconName: 'workspaces',
    keywords: ['workspaces', 'all workspaces', 'home'],
  })

  // De-dupe by href (e.g. newsletters parent/child both resolve to same hub).
  const seen = new Set<string>()
  return entries.filter(entry => {
    const key = `${entry.href}::${entry.labelKey}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}
